import React, { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContentText,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  SwapVert as SwapVertIcon,
} from '@mui/icons-material';
import { useDebounce } from '../hooks/useDebounce';
import { useToast } from '../hooks/useToast';
import {
  authService,
  stockService,
  type RegisterStockMovementRequest,
  type StockItem,
  type StockMovement,
  type StockMovementSource,
  type StockMovementType,
} from '../services';

const MOVEMENT_TYPES: StockMovementType[] = [
  'ENTRY',
  'SALE_CONSUMPTION',
  'INTERNAL_CONSUMPTION',
  'TRANSFER_OUT',
  'TRANSFER_IN',
  'ADJUSTMENT_POSITIVE',
  'ADJUSTMENT_NEGATIVE',
];

const MOVEMENT_SOURCES: StockMovementSource[] = ['MANUAL', 'ORDER', 'CLOSURE', 'TRANSFER', 'PRODUCTION', 'SYSTEM'];

const ENTRY_TYPES: StockMovementType[] = ['ENTRY', 'TRANSFER_IN', 'ADJUSTMENT_POSITIVE'];

interface MovementFilters {
  itemId: string;
  unitId: string;
  type: '' | StockMovementType;
  startDate: string;
  endDate: string;
  limit: string;
}

interface MovementFormData {
  itemId: string;
  unit: string;
  quantity: string;
  type: StockMovementType;
  source: StockMovementSource;
  unitId: string;
  referenceId: string;
  referenceCode: string;
  notes: string;
  eventDate: string;
  costUnit: string;
  costTotal: string;
  currency: string;
  lotId: string;
  batchCode: string;
  expirationDate: string;
}

const DEFAULT_FORM: MovementFormData = {
  itemId: '',
  unit: 'UN',
  quantity: '',
  type: 'ENTRY',
  source: 'MANUAL',
  unitId: '',
  referenceId: '',
  referenceCode: '',
  notes: '',
  eventDate: '',
  costUnit: '',
  costTotal: '',
  currency: 'BRL',
  lotId: '',
  batchCode: '',
  expirationDate: '',
};

const StockMovementsPage: React.FC = () => {
  const userRole = authService.getUserRole();
  const { showError, showSuccess } = useToast();

  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<MovementFilters>({
    itemId: '',
    unitId: '',
    type: '',
    startDate: '',
    endDate: '',
    limit: '50',
  });
  const [cnpjFilter, setCnpjFilter] = useState('');
  const debouncedItemFilter = useDebounce(filters.itemId, 300);

  const [formOpen, setFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formData, setFormData] = useState<MovementFormData>(DEFAULT_FORM);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [movementToDelete, setMovementToDelete] = useState<StockMovement | null>(null);

  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [stockItemsLoading, setStockItemsLoading] = useState(false);
  const [stockItemsDialogOpen, setStockItemsDialogOpen] = useState(false);
  const [stockItemsSearch, setStockItemsSearch] = useState('');
  const [itemSearchInput, setItemSearchInput] = useState('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState<StockMovement | null>(null);

  const canFilterByCnpj = userRole === 'DEVELOPER';

  const loadMovements = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, unknown> = {
        itemId: debouncedItemFilter.trim() || undefined,
        unitId: filters.unitId.trim() || undefined,
        type: filters.type || undefined,
        startDate: filters.startDate ? new Date(filters.startDate).toISOString() : undefined,
        endDate: filters.endDate ? new Date(filters.endDate).toISOString() : undefined,
        limit: filters.limit ? Number(filters.limit) : undefined,
        cnpj: canFilterByCnpj ? cnpjFilter.trim() || undefined : undefined,
      };

      const response = await stockService.listStockMovements(params);
      setMovements(response.items);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar movimentações de estoque');
    } finally {
      setLoading(false);
    }
  }, [filters, debouncedItemFilter, canFilterByCnpj, cnpjFilter]);

  useEffect(() => {
    loadMovements();
  }, [loadMovements]);

  const loadStockItems = useCallback(async () => {
    try {
      setStockItemsLoading(true);
      const params = canFilterByCnpj && cnpjFilter.trim() ? { cnpj: cnpjFilter.trim() } : undefined;
      const response = await stockService.listStockItems(params);
      const sorted = [...response].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
      setStockItems(sorted);
    } catch (err: any) {
      console.error('[StockMovementsPage] Erro ao carregar itens de estoque', err);
      showError(err.message || 'Erro ao carregar itens de estoque');
    } finally {
      setStockItemsLoading(false);
    }
  }, [canFilterByCnpj, cnpjFilter, showError]);

  useEffect(() => {
    if (formOpen) {
      void loadStockItems();
    }
  }, [formOpen, loadStockItems]);

  useEffect(() => {
    if (stockItemsDialogOpen) {
      void loadStockItems();
    }
  }, [stockItemsDialogOpen, loadStockItems]);

  const movementTypeLabel = useMemo(() => {
    const map: Record<StockMovementType, string> = {
      ENTRY: 'Entrada',
      SALE_CONSUMPTION: 'Saída (venda)',
      INTERNAL_CONSUMPTION: 'Saída interna',
      TRANSFER_OUT: 'Transferência saída',
      TRANSFER_IN: 'Transferência entrada',
      ADJUSTMENT_POSITIVE: 'Ajuste +',
      ADJUSTMENT_NEGATIVE: 'Ajuste -',
    };
    return map;
  }, []);

  const movementSourceLabel = useMemo(() => {
    const map: Record<StockMovementSource, string> = {
      MANUAL: 'Manual',
      ORDER: 'Pedido',
      CLOSURE: 'Inventário',
      TRANSFER: 'Transferência',
      PRODUCTION: 'Produção',
      SYSTEM: 'Sistema',
    };
    return map;
  }, []);

  const handleFilterChange = (field: keyof MovementFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenForm = () => {
    setFormOpen(true);
    setFormData(DEFAULT_FORM);
    setFormError(null);
    setItemSearchInput('');
    setStockItemsSearch('');
    setStockItemsDialogOpen(false);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setFormData(DEFAULT_FORM);
    setFormError(null);
    setItemSearchInput('');
    setStockItemsSearch('');
    setStockItemsDialogOpen(false);
  };

  const selectedStockItem = useMemo(
    () => stockItems.find((item) => item.id === formData.itemId) ?? null,
    [stockItems, formData.itemId],
  );

  useEffect(() => {
    if (selectedStockItem) {
      const label = `${selectedStockItem.name}${selectedStockItem.sku ? ` • ${selectedStockItem.sku}` : ''}`;
      if (itemSearchInput !== label) {
        setItemSearchInput(label);
      }
    } else if (formData.itemId && itemSearchInput === '') {
      setItemSearchInput(formData.itemId);
    }
  }, [selectedStockItem, formData.itemId, itemSearchInput]);

  const filteredStockItems = useMemo(() => {
    const term = stockItemsSearch.trim().toLowerCase();
    if (!term) {
      return stockItems;
    }

    return stockItems.filter((item) => {
      const tokens = [item.name, item.sku, item.id, item.unit]
        .filter(Boolean)
        .map((value) => value!.toString().toLowerCase());
      return tokens.some((token) => token.includes(term));
    });
  }, [stockItems, stockItemsSearch]);

  const handleSubmitForm = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormLoading(true);
    setFormError(null);

    const quantity = Number(formData.quantity.replace(',', '.'));
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setFormError('Quantidade inválida.');
      setFormLoading(false);
      return;
    }

    const costUnitValue = formData.costUnit ? Number(formData.costUnit.replace(',', '.')) : undefined;
    const costTotalValue = formData.costTotal ? Number(formData.costTotal.replace(',', '.')) : undefined;

    if (ENTRY_TYPES.includes(formData.type) && (!costUnitValue || costUnitValue <= 0)) {
      setFormError('Movimentos de entrada exigem custo unitário.');
      setFormLoading(false);
      return;
    }

    if ((costUnitValue && costUnitValue < 0) || (costTotalValue && costTotalValue < 0)) {
      setFormError('Custos informados devem ser positivos.');
      setFormLoading(false);
      return;
    }

    const payload: RegisterStockMovementRequest = {
      itemId: formData.itemId.trim(),
      unit: formData.unit.trim() || 'UN',
      quantity,
      type: formData.type,
      source: formData.source,
      unitId: formData.unitId.trim() || undefined,
      referenceId: formData.referenceId.trim() || undefined,
      referenceCode: formData.referenceCode.trim() || undefined,
      notes: formData.notes.trim() || undefined,
      eventDate: formData.eventDate ? new Date(formData.eventDate).toISOString() : undefined,
      metadata: undefined,
      costInfo: undefined,
    };

    if (!payload.itemId) {
      setFormError('Selecione o item de estoque.');
      setFormLoading(false);
      return;
    }

    const costInfo: Record<string, unknown> = {};
    if (costUnitValue !== undefined) {
      costInfo.unitCost = costUnitValue;
    }
    if (costTotalValue !== undefined) {
      costInfo.totalCost = costTotalValue;
    }
    if (formData.currency.trim()) {
      costInfo.currency = formData.currency.trim();
    }
    if (formData.lotId.trim()) {
      costInfo.lotId = formData.lotId.trim();
    }
    if (formData.batchCode.trim()) {
      costInfo.batchCode = formData.batchCode.trim();
    }
    if (formData.expirationDate) {
      costInfo.expirationDate = new Date(formData.expirationDate).toISOString();
    }

    if (Object.keys(costInfo).length > 0) {
      payload.costInfo = costInfo;
    }

    if (canFilterByCnpj && cnpjFilter.trim()) {
      payload.metadata = { cnpj: cnpjFilter.trim() };
    }

    try {
      await stockService.registerStockMovement(payload);
      showSuccess('Movimentação registrada com sucesso.');
      handleCloseForm();
      loadMovements();
    } catch (err: any) {
      setFormError(err.message || 'Erro ao registrar movimentação');
    } finally {
      setFormLoading(false);
    }
  };

  const handleOpenDeleteDialog = (movement: StockMovement) => {
    setMovementToDelete(movement);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setMovementToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!movementToDelete) {
      return;
    }

    try {
      // Ainda não existe endpoint dedicado; manter placeholder
      showError('Remoção manual de movimentos não suportada no momento.');
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleOpenDetailsDialog = (movement: StockMovement) => {
    setSelectedMovement(movement);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedMovement(null);
  };

  const formatDateTime = (value: string | undefined): string => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : new Intl.DateTimeFormat('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }).format(date);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2, px: { xs: 1, sm: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Movimentações de Estoque
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Consulte entradas, saídas e ajustes e registre novos lançamentos manuais.
          </Typography>
        </Box>

        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenForm}>
          Nova movimentação
        </Button>
      </Box>

      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(6, minmax(0, 1fr))',
            },
            alignItems: 'flex-end',
          }}
        >
          <TextField
            label="ID do item"
            value={filters.itemId}
            onChange={(event: ChangeEvent<HTMLInputElement>) => handleFilterChange('itemId', event.target.value)}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SwapVertIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="ID da unidade"
            value={filters.unitId}
            onChange={(event: ChangeEvent<HTMLInputElement>) => handleFilterChange('unitId', event.target.value)}
            size="small"
          />

          <FormControl size="small">
            <InputLabel>Tipo</InputLabel>
            <Select
              value={filters.type}
              label="Tipo"
              onChange={(event: SelectChangeEvent<string>) =>
                handleFilterChange('type', event.target.value as MovementFilters['type'])
              }
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              {MOVEMENT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {movementTypeLabel[type]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Data inicial"
            type="date"
            value={filters.startDate}
            onChange={(event: ChangeEvent<HTMLInputElement>) => handleFilterChange('startDate', event.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Data final"
            type="date"
            value={filters.endDate}
            onChange={(event: ChangeEvent<HTMLInputElement>) => handleFilterChange('endDate', event.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Limite"
            type="number"
            value={filters.limit}
            onChange={(event: ChangeEvent<HTMLInputElement>) => handleFilterChange('limit', event.target.value)}
            size="small"
            inputProps={{ min: 1, max: 200 }}
          />

          {canFilterByCnpj && (
            <TextField
              label="CNPJ"
              value={cnpjFilter}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setCnpjFilter(event.target.value)}
              size="small"
              placeholder="Somente números"
              sx={{ gridColumn: { xs: 'auto', md: 'span 2' } }}
            />
          )}

          <Box sx={{ gridColumn: { xs: 'auto', md: 'span 1' } }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadMovements} fullWidth>
              Atualizar
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={1}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Qtd</TableCell>
                  <TableCell>Unidade</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell>Referência</TableCell>
                  <TableCell>Usuário</TableCell>
                  <TableCell align="right">Custo total</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.map((movement) => (
                  <TableRow key={movement.id} hover>
                    <TableCell>{formatDateTime(movement.eventDate)}</TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="subtitle2">{movement.itemId}</Typography>
                        {movement.unitId && (
                          <Typography variant="caption" color="text.secondary">
                            Unidade: {movement.unitId}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{movement.quantity.toLocaleString('pt-BR')}</TableCell>
                    <TableCell>{movement.unit}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={movementTypeLabel[movement.type]}
                        color={ENTRY_TYPES.includes(movement.type) ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>{movementSourceLabel[movement.source]}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{movement.referenceCode || '-'}</Typography>
                      {movement.referenceId && (
                        <Typography variant="caption" color="text.secondary">
                          #{movement.referenceId}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{movement.createdBy || '-'}</TableCell>
                    <TableCell align="right">
                      {movement.costInfo?.totalCost !== undefined
                        ? movement.costInfo.totalCost.toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: movement.costInfo.currency || 'BRL',
                          })
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Detalhes">
                        <IconButton size="small" onClick={() => handleOpenDetailsDialog(movement)}>
                          <SwapVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(movement)}
                            disabled
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {movements.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        Nenhuma movimentação encontrada para os filtros selecionados.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="md" fullWidth>
        <DialogTitle>Registrar movimentação</DialogTitle>
        <form onSubmit={handleSubmitForm}>
          <DialogContent dividers>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}

            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(4, minmax(0, 1fr))',
                },
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
                  gap: 1,
                  alignItems: 'center',
                  gridColumn: { xs: 'auto', md: 'span 2' },
                }}
              >
                <Autocomplete
                  fullWidth
                  freeSolo
                  options={stockItems}
                  loading={stockItemsLoading}
                  value={selectedStockItem}
                  inputValue={itemSearchInput}
                  onChange={(_, value) => {
                    if (!value) {
                      setFormData((prev) => ({ ...prev, itemId: '' }));
                      setItemSearchInput('');
                      return;
                    }

                    if (typeof value === 'string') {
                      setFormData((prev) => ({ ...prev, itemId: value }));
                      setItemSearchInput(value);
                      return;
                    }

                    setFormData((prev) => ({
                      ...prev,
                      itemId: value.id,
                      unit: value.unit || prev.unit,
                    }));
                    setItemSearchInput(
                      `${value.name}${value.sku ? ` • ${value.sku}` : ''}`,
                    );
                  }}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') {
                      setItemSearchInput(value);
                      setFormData((prev) => ({ ...prev, itemId: value.trim() }));
                    }

                    if (reason === 'clear') {
                      setItemSearchInput('');
                      setFormData((prev) => ({ ...prev, itemId: '' }));
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) =>
                    typeof option === 'string'
                      ? option
                      : `${option.name}${option.sku ? ` • ${option.sku}` : ''} (${option.unit || 'UN'})`
                  }
                  noOptionsText={stockItemsLoading ? 'Carregando...' : 'Nenhum item encontrado'}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="body2" fontWeight={600}>
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {option.id}
                          {option.sku ? ` • SKU: ${option.sku}` : ''} • Unidade: {option.unit || 'UN'}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Item de estoque"
                      margin="dense"
                      required
                      placeholder="Busque por nome, SKU ou ID"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {stockItemsLoading ? (
                              <CircularProgress color="inherit" size={18} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  disabled={formLoading}
                />
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={() => {
                    setStockItemsDialogOpen(true);
                    setStockItemsSearch('');
                  }}
                  disabled={formLoading}
                >
                  Buscar item
                </Button>
              </Box>
              <TextField
                label="Quantidade"
                value={formData.quantity}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, quantity: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
              />
              <TextField
                label="Unidade"
                value={formData.unit}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, unit: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
              />
              <FormControl margin="dense" disabled={formLoading}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={formData.type}
                  label="Tipo"
                  onChange={(event: SelectChangeEvent<string>) =>
                    setFormData((prev) => ({ ...prev, type: event.target.value as StockMovementType }))
                  }
                >
                  {MOVEMENT_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {movementTypeLabel[type]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl margin="dense" disabled={formLoading}>
                <InputLabel>Origem</InputLabel>
                <Select
                  value={formData.source}
                  label="Origem"
                  onChange={(event: SelectChangeEvent<string>) =>
                    setFormData((prev) => ({ ...prev, source: event.target.value as StockMovementSource }))
                  }
                >
                  {MOVEMENT_SOURCES.map((source) => (
                    <MenuItem key={source} value={source}>
                      {movementSourceLabel[source]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="ID da unidade"
                value={formData.unitId}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, unitId: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
              />
              <TextField
                label="Data do evento"
                type="datetime-local"
                value={formData.eventDate}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, eventDate: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Referência ID"
                value={formData.referenceId}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, referenceId: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
              />
              <TextField
                label="Referência código"
                value={formData.referenceCode}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, referenceCode: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
              />

              <TextField
                label="Observações"
                value={formData.notes}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, notes: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
                multiline
                minRows={2}
                sx={{ gridColumn: { xs: 'auto', md: 'span 2' } }}
              />

              <Box sx={{ gridColumn: '1 / -1' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formData.costUnit || formData.costTotal)}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setFormData((prev) => ({
                          ...prev,
                          costUnit: event.target.checked ? prev.costUnit || '' : '',
                          costTotal: event.target.checked ? prev.costTotal || '' : '',
                        }))
                      }
                      disabled={formLoading}
                    />
                  }
                  label="Informações de custo"
                />
              </Box>

              <TextField
                label="Custo unitário"
                value={formData.costUnit}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, costUnit: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
                placeholder="Obrigatório para entradas"
              />
              <TextField
                label="Custo total"
                value={formData.costTotal}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, costTotal: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
              />
              <TextField
                label="Moeda"
                value={formData.currency}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, currency: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
              />
              <TextField
                label="Lote"
                value={formData.lotId}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, lotId: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
              />
              <TextField
                label="Código do lote"
                value={formData.batchCode}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, batchCode: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
              />
              <TextField
                label="Validade"
                type="date"
                value={formData.expirationDate}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({ ...prev, expirationDate: event.target.value }))
                }
                disabled={formLoading}
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForm} disabled={formLoading}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={formLoading}>
              {formLoading ? 'Salvando...' : 'Registrar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir movimentação</DialogTitle>
        <DialogContent dividers>
          Ainda não é possível remover uma movimentação manualmente via painel. Entre em contato com o suporte.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Fechar</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Entendi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={stockItemsDialogOpen}
        onClose={() => setStockItemsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Selecionar item de estoque</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Buscar"
              placeholder="Digite nome, SKU, unidade ou ID"
              value={stockItemsSearch}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setStockItemsSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto' }}>
              {stockItemsLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <CircularProgress size={28} />
                </Box>
              ) : filteredStockItems.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhum item encontrado.
                  </Typography>
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Unidade</TableCell>
                      <TableCell>ID</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStockItems.map((item) => (
                      <TableRow
                        key={item.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            itemId: item.id,
                            unit: item.unit || prev.unit,
                          }));
                          setItemSearchInput(`${item.name}${item.sku ? ` • ${item.sku}` : ''}`);
                          setStockItemsDialogOpen(false);
                          setStockItemsSearch('');
                        }}
                      >
                        <TableCell>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="body2" fontWeight={600}>
                              {item.name}
                            </Typography>
                            {item.notes && (
                              <Typography variant="caption" color="text.secondary">
                                {item.notes}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{item.sku || '-'}</TableCell>
                        <TableCell>{item.unit || 'UN'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {item.id}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStockItemsDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailsDialogOpen} onClose={handleCloseDetailsDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Detalhes da movimentação</DialogTitle>
        <DialogContent dividers>
          {selectedMovement ? (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2">Data</Typography>
                <Typography variant="body2">{formatDateTime(selectedMovement.eventDate)}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Item</Typography>
                <Typography variant="body2">{selectedMovement.itemId}</Typography>
              </Box>
              {selectedMovement.unitId && (
                <Box>
                  <Typography variant="subtitle2">ID da unidade</Typography>
                  <Typography variant="body2">{selectedMovement.unitId}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle2">Quantidade</Typography>
                <Typography variant="body2">
                  {selectedMovement.quantity.toLocaleString('pt-BR')} {selectedMovement.unit}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Tipo</Typography>
                <Typography variant="body2">{movementTypeLabel[selectedMovement.type]}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">Origem</Typography>
                <Typography variant="body2">{movementSourceLabel[selectedMovement.source]}</Typography>
              </Box>
              {(selectedMovement.referenceCode || selectedMovement.referenceId) && (
                <Box>
                  <Typography variant="subtitle2">Referência</Typography>
                  <Typography variant="body2">
                    {selectedMovement.referenceCode || '-'}
                    {selectedMovement.referenceId ? ` • #${selectedMovement.referenceId}` : ''}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="subtitle2">Registrado por</Typography>
                <Typography variant="body2">{selectedMovement.createdBy || '-'}</Typography>
              </Box>
              {selectedMovement.costInfo && (
                <Box>
                  <Typography variant="subtitle2">Custos</Typography>
                  <DialogContentText component="div" sx={{ display: 'flex', flexDirection: 'column' }}>
                    {selectedMovement.costInfo.totalCost !== undefined && (
                      <Typography variant="body2">
                        Total: {selectedMovement.costInfo.totalCost.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: selectedMovement.costInfo.currency || 'BRL',
                        })}
                      </Typography>
                    )}
                    {selectedMovement.costInfo.unitCost !== undefined && (
                      <Typography variant="body2">
                        Unitário: {selectedMovement.costInfo.unitCost.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: selectedMovement.costInfo.currency || 'BRL',
                        })}
                      </Typography>
                    )}
                    {selectedMovement.costInfo.currency && selectedMovement.costInfo.currency !== 'BRL' && (
                      <Typography variant="body2">Moeda: {selectedMovement.costInfo.currency}</Typography>
                    )}
                    {selectedMovement.costInfo.batchCode && (
                      <Typography variant="body2">Lote: {selectedMovement.costInfo.batchCode}</Typography>
                    )}
                    {selectedMovement.costInfo.lotId && (
                      <Typography variant="body2">ID do lote: {selectedMovement.costInfo.lotId}</Typography>
                    )}
                    {selectedMovement.costInfo.expirationDate && (
                      <Typography variant="body2">
                        Validade: {formatDateTime(selectedMovement.costInfo.expirationDate)}
                      </Typography>
                    )}
                  </DialogContentText>
                </Box>
              )}
              {selectedMovement.notes && (
                <Box>
                  <Typography variant="subtitle2">Observações</Typography>
                  <Typography variant="body2">{selectedMovement.notes}</Typography>
                </Box>
              )}
            </Box>
          ) : (
            <DialogContentText>Nenhuma movimentação selecionada.</DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsDialog}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StockMovementsPage;
